import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import cn from 'clsx';

import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { CreatePollData } from '../types';

import ButtonMighty from '@components/elements/ButtonMighty';
import Card from '@components/elements/Card';
import InputTextarea from '@components/elements/InputTextarea';
import PollCreateQuestionOptionCard from './QuestionOptionCard';
import useSwr from 'swr';
import {
  getPackageByTier,
  getPackageByUser,
} from '@sdk/managers/PackageManager';
import { Tier } from '@sdk/API';
import { useAuth } from '@hooks/Auth';
import { Colors } from '@theme/index';
import InputText from '@components/elements/InputText';
import { makeStyles } from '@mui/styles';
const useStyles = (neon) =>
  makeStyles({
    cbutton: {
      border: `solid 2px ${neon} !important`,
      '&:hover': {
        border: 'solid 2px transparent !important',
        background: '#FF46FF !important',
        color: 'black !important',
      },
    },
  });

interface PollCreateQuestionFormProps {
  pledging?: boolean;
  toPledge: boolean;
  isEdit?: boolean;
  isGroup?: boolean;
  loading?: boolean;
  index: number;
  onClose: () => void;
  neon?: string;
}

const PollCreateQuestionForm: React.FC<PollCreateQuestionFormProps> = ({
  pledging = false,
  isEdit = false,
  isGroup = false,
  loading = false,
  index,
  toPledge,
  onClose,
  neon,
}) => {
  const classes = useStyles(neon)();
  const { dbUser, loading: authLoading } = useAuth();
  const form = useFormContext<CreatePollData>();
  const formPremium = form.watch('isPremium');
  const formCompany = form.watch('company');
  const [isExact, setExact] = useState(
    isGroup && index > 0
      ? !form.getValues(`questions.${index}.isNotExact`)
      : !form.getValues('isNotExact')
  );
  const { data: packageSub } = useSwr(
    !authLoading && dbUser?.username
      ? ['/permissions', dbUser?.username, formPremium, formCompany]
      : null,
    async (_query, username, isPremium, isCompany) => {
      let packageSub: any;
      if (isPremium) packageSub = await getPackageByTier(Tier.PREMIUM);
      else if (isCompany) packageSub = await getPackageByTier(Tier.ENTERPRISE);
      else packageSub = await getPackageByUser(username);

      if (!packageSub) packageSub = await getPackageByTier(Tier.FREE);

      return packageSub;
    },
    {
      revalidateOnFocus: true,
      onError: (err, key) => {
        console.log('Failed to fetch permission', { key }, err);
      },
    }
  );

  const { fields, append, remove, swap, replace } = useFieldArray({
    name: `questions.${index}.options` as const,
  });
  // const [count, setCount] = useState(0);
  // useEffect(() => {
  //   if (!count) {
  //     setCount(1);
  //     return;
  //   }
  //   if (toPledge && pledging) replace([{ title: '' }  replace([{ title: form.getValues(`questions.${index}.question`) || '' }]);
  //   else {
  //     replace([{ title: '' }, { title: '' }]);
  //   }
  // }, [toPledge, pledging, count, replace]);

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    swap(result.source.index, result.destination.index);
  };

  return (
    <div className="flex flex-col py-8 space-y-8">
      <Card bgColor="white" shadowColor="purple" neon={neon}>
        <InputTextarea
          neon={neon}
          className="py-4 text-xl font-bold border-none"
          placeholder={'Question Title*'}
          maxLength={80}
          {...form.register(`questions.${index}.question` as const)}
          onChange={(event) => {
            if (toPledge && pledging)
              form.setValue(
                `questions.${0}.options.${0}.title`,
                event.target.value
              );
          }}
        />
      </Card>

      <div>
        {fields?.map((field, optIndex) => {
          return (
            <PollCreateQuestionOptionCard
              neon={neon}
              id={field.id}
              key={field.id}
              index={optIndex}
              pledging={pledging}
              questionIndex={index}
              register={form.register}
              control={form.control}
              hideResponse={toPledge && pledging}
              // className={optIndex !== fields.length - 1 ? 'mb-24' : ''}
              onRemove={() => {
                if (toPledge && pledging)
                  toast.error('You must have at least 1 options.');
                else if (fields.length <= 2)
                  toast.error('You must have at least 2 options.');
                else {
                  remove(optIndex);
                }
              }}
            />
          );
        })}
      </div>
      {(!toPledge || !pledging) && (
        <Card
          bgColor="white"
          className="py-4"
          neon={neon}
          style={{ borderBottom: '2px solid ' + neon }}
        >
          <div className="flex flex-row w-full mb-2 items-between">
            <h2 className="w-full mb-1 mr-4 text-2xl text-white">
              Maximum Number Of Selection By User
            </h2>
          </div>
          <div className="flex items-center">
            {' '}
            <div
              className="flex items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setExact(true);
                form.setValue(
                  isGroup && index > 0
                    ? `questions.${index}.isNotExact`
                    : 'isNotExact',
                  false
                );
              }}
            >
              {' '}
              <input
                type="radio"
                checked={isExact}
                style={{ width: '24px', height: '24px' }}
              />{' '}
              <span className="text-white ml-1">Exact</span>
            </div>
            <div
              className="flex items-center ml-5"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setExact(false);
                form.setValue(
                  isGroup && index > 0
                    ? `questions.${index}.isNotExact`
                    : 'isNotExact',
                  true
                );
              }}
            >
              {' '}
              <input
                type="radio"
                checked={!isExact}
                style={{ width: '24px', height: '24px' }}
              />{' '}
              <span className="text-white ml-1">Maximum Options</span>
            </div>
          </div>
          <InputText
            neon={true}
            className="mt-3"
            type="number"
            {...form.register(
              isGroup && index > 0 ? `questions.${index}.maxVote` : 'maxVote'
            )}
            borderColor={Colors['gray-dark']}
            shadow={false}
            min={1}
            max={fields.length}
          />
        </Card>
      )}

      {!(toPledge && pledging) && (
        <button
          type="button"
          style={{
            color: neon,
            position: 'absolute',
            bottom: -50,
            left: 0,
            width: '230px',
            height: 55,
            opacity:
              packageSub && fields.length >= packageSub?.pollOptionLimit
                ? 0.6
                : 1,
            pointerEvents:
              packageSub && fields.length >= packageSub?.pollOptionLimit
                ? 'none'
                : 'all',
          }}
          className={
            'px-3 py-4 text-lg flex items-center font-bold font-display  focus:outline-none ' +
            classes.cbutton
          }
          onClick={() => {
            append({
              title: '',
              description: '',
            });
          }}
          disabled={packageSub && fields.length >= packageSub?.pollOptionLimit}
        >
          Add Response{' '}
          <span
            className={cn('text-white', {
              'text-magenta': packageSub
                ? fields.length - packageSub.pollOptionLimit > 0
                : false,
            })}
          >
            {packageSub
              ? `(${fields.length}/${packageSub?.pollOptionLimit})`
              : ''}
          </span>
        </button>
      )}

      {isGroup && (
        <div className="flex flex-col space-y-4">
          <div>
            <ButtonMighty
              neon={neon}
              disabled={loading}
              loading={loading}
              color="#29FF2E"
              style={{
                position: 'absolute',
                bottom: -86,
                right: 0,
                width: 'calc(100% - 240px)',
                background: neon,
                color: 'black',
              }}
              text="Back to Questions List"
              onClick={() => {
                form.trigger('questions');
                onClose();
              }}
              borderColor="#4400FF"
              margin="0"
              textColor="#4400FF"
            />
          </div>
          {/* {index !== 0 && (
            <div>
              <ButtonMighty
                color="red"
                text="Remove Question"
                onClick={() => {
                  questions.remove(index);
                  onClose();
                }}
                borderColor="#108EE9"
                margin="0"
                textColor="yellow"
              />
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

export default PollCreateQuestionForm;
